import type Phaser from "phaser";
import { CTF_BASE_RADIUS, CTF_FLAG_RADIUS, type ShipColor, type WorldState } from "../../sim";

/** Player colours for objective drawings (the CSS --accent / --accent-2 of the ships). */
export const PLAYER_TINT: Record<ShipColor, number> = {
	blue: 0x3fa9ff,
	red: 0xff4d5e,
	green: 0x3fd46b,
	yellow: 0xffd23f,
};

/**
 * Draws the objective of King of the Hill / Capture the Flag (P11.2) into a Graphics object.
 * `at(x, y)` maps a world position to the graphics' coordinates (or null if off-screen),
 * `scale` is pixels per world unit. Flags carried by a ship are drawn at `carried(player)`
 * (the ship's interpolated sprite) so they don't lag behind it.
 */
export function drawObjective(
	g: Phaser.GameObjects.Graphics,
	world: WorldState,
	at: (x: number, y: number) => { x: number; y: number },
	scale: number,
	carried: (player: number) => { x: number; y: number } | null,
	minimap = false,
): void {
	const o = world.objective;
	if (!o) return;
	if (o.kind === "koth") {
		const c = at(o.zone.x, o.zone.y);
		const holder = o.holder === null ? null : world.players[o.holder];
		const tint = holder ? PLAYER_TINT[holder.color] : 0xffffff;
		const outer = o.zone.outer * scale;
		const inner = o.zone.inner * scale;
		g.fillStyle(tint, holder ? 0.1 : 0.05);
		g.fillCircle(c.x, c.y, outer);
		g.lineStyle(minimap ? 1 : 3, tint, holder ? 0.85 : 0.45);
		g.strokeCircle(c.x, c.y, outer);
		if (inner > 0) {
			g.lineStyle(minimap ? 1 : 2, tint, 0.35);
			g.strokeCircle(c.x, c.y, inner);
		}
		return;
	}
	if (o.kind !== "ctf") return; // score limit and 2008 survival have nothing to draw
	for (const flag of o.flags) {
		const color = PLAYER_TINT[world.players[flag.owner]?.color ?? "blue"];
		const base = at(flag.home.x, flag.home.y);
		g.lineStyle(minimap ? 1 : 2, color, 0.6);
		g.strokeCircle(base.x, base.y, Math.max(minimap ? 3 : 0, CTF_BASE_RADIUS * scale));
		const pos = (flag.carrier !== null && carried(flag.carrier)) || at(flag.pos.x, flag.pos.y);
		if (minimap) {
			g.fillStyle(0x000000, 0.7).fillCircle(pos.x, pos.y, 3.5);
			g.fillStyle(color, 1).fillCircle(pos.x, pos.y, 2.5);
			continue;
		}
		// A pennant on a pole, sized like the pickup radius.
		const h = CTF_FLAG_RADIUS * 2 * scale;
		const px = pos.x - h * 0.25;
		const top = pos.y - h * 0.6;
		g.lineStyle(Math.max(1.5, 3 * scale), 0xffffff, 0.9);
		g.lineBetween(px, top, px, pos.y + h * 0.4);
		g.fillStyle(color, flag.atHome ? 1 : 0.85);
		g.fillTriangle(px, top, px + h * 0.75, top + h * 0.22, px, top + h * 0.44);
	}
}
